import type React from "react";
import { fmtInt, fmtPct } from "@/lib/format";
import type { Slide, SlideContext } from "../slide.types";
import { Tile } from "../visuals/Tile";
import { Card, CardLabel } from "../visuals/Card";
import { DonutWithLegend, type DonutDatum } from "../visuals/DonutWithLegend";
import { TopBars, type TopBarItem } from "../visuals/TopBars";
import { ACCENT_GREEN, ACCENT_ORANGE, ACCENT_RED } from "../colors";

function CheckinSlideView({ data, resetKey }: SlideContext): React.JSX.Element {
  const ci = data.checkin;
  if (ci === null) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/40 text-sm font-body">
          No check-in data uploaded for this period.
        </p>
      </div>
    );
  }

  const { total_checkins, overall_completion_rate, by_store, avg_duration_minutes } = ci;

  const completionAccent =
    overall_completion_rate === null
      ? ACCENT_ORANGE
      : overall_completion_rate >= 80
        ? ACCENT_GREEN
        : overall_completion_rate >= 50
          ? ACCENT_ORANGE
          : ACCENT_RED;

  // Completed vs missed donut — derived from by_store totals
  const totalCompleted = by_store.reduce((s, r) => s + (r.completed ?? 0), 0);
  const totalMissed = by_store.reduce((s, r) => s + (r.missed ?? 0), 0);

  const donutData: DonutDatum[] = [
    { name: "Completed", value: totalCompleted, color: ACCENT_GREEN },
    { name: "Missed", value: totalMissed, color: ACCENT_RED },
  ].filter((d) => d.value > 0);

  const topStores: TopBarItem[] = [...by_store]
    .sort((a, b) => b.checkins - a.checkins)
    .slice(0, 5)
    .map((s) => ({
      label: s.store,
      value: s.checkins,
      display: fmtInt(s.checkins),
    }));

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <Tile
          label="Total Check-ins"
          value={total_checkins}
          format={(v) => fmtInt(Math.round(v))}
          delay={100}
          accent={ACCENT_GREEN}
          resetKey={resetKey}
        />
        <Tile
          label="Completion Rate"
          value={overall_completion_rate ?? 0}
          format={(v) =>
            overall_completion_rate === null ? "—" : fmtPct(v)
          }
          delay={200}
          accent={completionAccent}
          resetKey={resetKey}
        />
        <Tile
          label="Avg Duration"
          value={avg_duration_minutes ?? 0}
          format={(v) =>
            avg_duration_minutes === null ? "—" : `${Math.round(v)}m`
          }
          delay={300}
          resetKey={resetKey}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {donutData.length > 0 ? (
          <DonutWithLegend
            title="Completed vs Missed"
            data={donutData}
            centerValue={total_checkins}
            centerLabel="check-ins"
            delay={400}
            accentCenter={completionAccent}
          />
        ) : (
          <Card delay={400}>
            <CardLabel>Completed vs Missed</CardLabel>
            <p className="text-white/40 text-sm font-body mt-6">
              Completion breakdown not available.
            </p>
          </Card>
        )}
        <TopBars
          title="Top Teams by Check-in Volume"
          items={topStores}
          delay={500}
          accentTop={ACCENT_GREEN}
          accentRest={ACCENT_ORANGE}
        />
      </div>
    </>
  );
}

export const checkinSlide: Slide = {
  id: "checkin",
  title: "Check-in Activity",
  render: (ctx) => <CheckinSlideView {...ctx} />,
};
